import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCcw,
  Mail,
  Phone,
  MapPin,
  Filter,
  Download,
} from "lucide-react";
import { authApi, saveToken, clearToken, getToken, formatApiErrorDetail } from "@/lib/authApi";
import { LOGOS } from "@/lib/constants";

const STATUSES = ["new", "contacted", "won", "lost"];
const STATUS_STYLE = {
  new: "bg-[#1E88E5]/15 text-[#42A5F5] border-[#1E88E5]/40",
  contacted: "bg-[#E6B445]/15 text-[#E6B445] border-[#E6B445]/40",
  won: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  lost: "bg-rose-500/15 text-rose-400 border-rose-500/40",
};

function fmtMoney(min, max, tier) {
  if (min == null) return "—";
  if (tier === "xlarge") return `$${min.toLocaleString()}+`;
  if (max == null || min === max) return `$${min.toLocaleString()}`;
  return `$${min.toLocaleString()} – $${max.toLocaleString()}`;
}

function fmtDate(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await authApi.post("/auth/login", { email, password });
      saveToken(data.access_token);
      toast.success(`Welcome back, ${data.user.name}`);
      onLogin(data.user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B1726] px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#142238] border border-slate-800 rounded-md p-8"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGOS.elephant} alt="" className="h-12 w-12" />
          <div>
            <div className="font-heading font-black uppercase tracking-tight text-white text-lg">
              All Day <span className="text-[#E6B445]">Admin</span>
            </div>
            <div className="text-slate-500 text-xs tracking-widest uppercase">
              Leads Dashboard
            </div>
          </div>
        </div>

        <label className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
          Email
        </label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email-input"
          className="mt-2 mb-5 w-full bg-[#0B1726] border border-slate-800 rounded-sm px-4 py-3 text-white focus:border-[#1E88E5] focus:ring-1 focus:ring-[#1E88E5] outline-none"
          placeholder="you@alldaypowerwashing.com"
        />
        <label className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
          Password
        </label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="mt-2 mb-6 w-full bg-[#0B1726] border border-slate-800 rounded-sm px-4 py-3 text-white focus:border-[#1E88E5] focus:ring-1 focus:ring-[#1E88E5] outline-none"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full bg-[#1E88E5] hover:bg-[#42A5F5] disabled:bg-slate-700 text-white font-heading font-bold tracking-wider uppercase py-3 rounded-sm transition-all"
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <p className="text-slate-500 text-xs mt-6 text-center">
          Authorized personnel only.
        </p>
      </form>
    </div>
  );
}

function LeadsTable({ user, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authApi.get("/leads");
      setLeads(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load leads");
      if (err?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const updateStatus = async (id, status) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await authApi.patch(`/leads/${id}`, { status });
      toast.success(`Marked ${status}`);
    } catch (err) {
      setLeads(prev);
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const counts = useMemo(() => {
    const c = { all: leads.length, new: 0, contacted: 0, won: 0, lost: 0 };
    leads.forEach((l) => {
      c[l.status] = (c[l.status] || 0) + 1;
    });
    return c;
  }, [leads]);

  const pipelineValue = useMemo(() => {
    return leads
      .filter((l) => ["new", "contacted", "won"].includes(l.status))
      .reduce((sum, l) => sum + (l.estimated_price_max || l.estimated_price_min || 0), 0);
  }, [leads]);

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  const exportCsv = () => {
    const header = [
      "Created",
      "Name",
      "Email",
      "Phone",
      "Service",
      "Property",
      "Size",
      "Est Min",
      "Est Max",
      "Address",
      "Status",
      "Heard via",
      "Notes",
    ];
    const rows = leads.map((l) => [
      l.created_at,
      l.name,
      l.email,
      l.phone,
      l.service,
      l.property_type,
      l.size_tier,
      l.estimated_price_min ?? "",
      l.estimated_price_max ?? "",
      l.address ?? "",
      l.status,
      l.referral_source ?? "",
      (l.message ?? "").replace(/\r?\n/g, " "),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `adpw-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0B1726] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-[#0B1726]/90 backdrop-blur-xl border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGOS.elephant} alt="" className="h-9 w-9" />
            <div className="leading-none">
              <div className="font-heading font-black uppercase tracking-tight">
                All Day <span className="text-[#E6B445]">Admin</span>
              </div>
              <div className="text-slate-500 text-[10px] tracking-widest uppercase mt-0.5">
                Leads Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="p-2 border border-slate-800 rounded-sm text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
              title="Refresh"
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-btn"
              className="p-2 border border-slate-800 rounded-sm text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
              title="Export CSV"
            >
              <Download className="w-4 h-4" />
            </button>
            <span className="hidden sm:block text-slate-500 text-sm px-3">
              {user.email}
            </span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 px-4 py-2 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-600 rounded-sm text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          {["all", "new", "contacted", "won", "lost"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`admin-filter-${k}`}
              className={`text-left p-5 rounded-md border transition-all ${
                filter === k
                  ? "border-[#1E88E5] bg-[#1E88E5]/10"
                  : "border-slate-800 bg-[#142238] hover:border-slate-600"
              }`}
            >
              <div className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
                {k === "all" ? "Total" : k}
              </div>
              <div className="font-heading text-3xl font-black mt-1">
                {counts[k] ?? 0}
              </div>
            </button>
          ))}
        </div>

        <div className="mb-6 bg-[#142238] border border-slate-800 rounded-md p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-[#E6B445]">
              Open pipeline value
            </div>
            <div className="font-heading text-2xl font-black text-white mt-1">
              ${pipelineValue.toLocaleString()}
            </div>
            <div className="text-slate-500 text-xs mt-1">
              Sum of estimates for new / contacted / won leads
            </div>
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Filter className="w-4 h-4" />
            Showing: <span className="text-white capitalize">{filter}</span> ({filtered.length})
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#142238] border border-slate-800 rounded-md overflow-hidden" data-testid="admin-leads-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-left">
                  <th className="px-4 py-3 font-heading text-xs font-bold tracking-widest uppercase text-slate-500">When</th>
                  <th className="px-4 py-3 font-heading text-xs font-bold tracking-widest uppercase text-slate-500">Customer</th>
                  <th className="px-4 py-3 font-heading text-xs font-bold tracking-widest uppercase text-slate-500">Service</th>
                  <th className="px-4 py-3 font-heading text-xs font-bold tracking-widest uppercase text-slate-500">Size</th>
                  <th className="px-4 py-3 font-heading text-xs font-bold tracking-widest uppercase text-slate-500">Estimate</th>
                  <th className="px-4 py-3 font-heading text-xs font-bold tracking-widest uppercase text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-10 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-10 text-center text-slate-500">
                      No leads in this view yet.
                    </td>
                  </tr>
                ) : (
                  filtered.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`admin-lead-row-${l.id}`}
                      className="border-b border-slate-900 hover:bg-[#0B1726]/40 transition-colors"
                    >
                      <td className="px-4 py-4 text-slate-400 whitespace-nowrap">
                        {fmtDate(l.created_at)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-heading font-bold text-white">{l.name}</div>
                        <div className="flex flex-col gap-0.5 mt-1 text-xs text-slate-400">
                          <a href={`tel:${l.phone}`} className="hover:text-white flex items-center gap-1.5">
                            <Phone className="w-3 h-3" /> {l.phone}
                          </a>
                          {l.email ? (
                            <a href={`mailto:${l.email}`} className="hover:text-white flex items-center gap-1.5">
                              <Mail className="w-3 h-3" /> {l.email}
                            </a>
                          ) : null}
                          {l.address ? (
                            <span className="flex items-start gap-1.5">
                              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>{l.address}</span>
                            </span>
                          ) : null}
                        </div>
                        {l.message ? (
                          <div className="text-slate-500 text-xs mt-2 italic max-w-xs">
                            "{l.message}"
                          </div>
                        ) : null}
                        {l.referral_source ? (
                          <div className="text-[10px] text-[#E6B445] font-heading font-bold tracking-widest uppercase mt-2">
                            Heard via: {l.referral_source}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-4 text-slate-300">
                        <div className="capitalize">{(l.service || "").replace(/-/g, " ")}</div>
                        <div className="text-xs text-slate-500 capitalize">{l.property_type}</div>
                      </td>
                      <td className="px-4 py-4 text-slate-300 capitalize">{l.size_tier}</td>
                      <td className="px-4 py-4 text-white font-heading font-bold">
                        {fmtMoney(l.estimated_price_min, l.estimated_price_max, l.size_tier)}
                      </td>
                      <td className="px-4 py-4">
                        <select
                          value={l.status}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          data-testid={`admin-status-select-${l.id}`}
                          className={`bg-transparent border rounded-sm px-2 py-1.5 text-xs font-bold uppercase tracking-widest font-heading outline-none focus:ring-1 focus:ring-[#1E88E5] ${
                            STATUS_STYLE[l.status] || STATUS_STYLE.new
                          }`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s} className="bg-[#0B1726] text-white">
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setChecking(false);
      return;
    }
    authApi
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = () => {
    clearToken();
    setUser(null);
    toast.success("Logged out");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0B1726] flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-page">
      <Helmet>
        <title>Admin — All Day Power Washing</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {user ? (
        <LeadsTable user={user} onLogout={handleLogout} />
      ) : (
        <LoginView onLogin={setUser} />
      )}
    </div>
  );
}
